<template>
  <div class="step-rule-type">
    <div class="step-header">
      <h2>{{ t('Qual tipo de pagamento?') }}</h2>
      <p class="step-description">
        {{ t('Escolha como este funcionário será pago baseado no seu trabalho.') }}
      </p>
    </div>

    <!-- Visual Rule Type Cards -->
    <div class="rule-types-grid">
      <button
        v-for="ruleType in ruleTypes"
        :key="ruleType.id"
        type="button"
        :data-testid="`payroll-payment-rules-step3-${ruleType.id}-card`"
        class="rule-type-card"
        :class="{ 'selected': wizardData.rule_type === ruleType.id }"
        @click="selectRuleType(ruleType.id)"
      >
        <div class="card-icon">
          <i :class="ruleType.icon"></i>
        </div>
        <div class="card-content">
          <h3>{{ ruleType.name }}</h3>
          <p class="card-description">{{ ruleType.description }}</p>
          <div class="card-example">
            <strong>{{ t('Exemplo:') }}</strong>
            <span>{{ ruleType.example }}</span>
          </div>
        </div>
        <div class="card-indicator"></div>
      </button>
    </div>

    <!-- Selected Rule Type Details -->
    <div v-if="wizardData.rule_type" class="selected-details">
      <div class="details-header">
        <i class="pi pi-info-circle"></i>
        <h3>{{ getSelectedRuleType().name }}</h3>
      </div>
      <div class="details-content">
        <p>{{ getSelectedRuleType().detailedDescription }}</p>
        <div class="details-example">
          <strong>{{ t('Como funciona:') }}</strong>
          <ul>
            <li v-for="point in getSelectedRuleType().howItWorks" :key="point">{{ point }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import type { Ref } from 'vue';
import { useI18n } from 'vue-i18n';

type Id = number | string;

interface WizardData {
  franchise_id?: Id;
  user_id?: Id;
  rule_type?: string;
  [key: string]: unknown;
}

interface WizardMachine {
  wizardData: Ref<WizardData>;
  next?: () => void;
}

interface RuleTypeCard {
  id: string;
  icon: string;
  name: string;
  description: string;
  example: string;
  detailedDescription: string;
  howItWorks: string[];
}

// Inject machine from parent
const machine = inject<WizardMachine | null>('wizardMachine', null);

const { t } = useI18n();

// Use wizard data from machine
const wizardData = computed<WizardData>(() => machine?.wizardData.value || {});

// PRESERVED: discriminator strings 'fixed' / 'commission_over_gross_revenue_*' must match backend rule types exactly.
const ruleTypes = computed<RuleTypeCard[]>(() => [
  {
    id: 'fixed',
    icon: 'pi pi-money-bill',
    name: t('Salário Fixo'),
    description: t('Valor mensal fixo, independente dos serviços realizados'),
    example: t('R$2.000 por mês'),
    detailedDescription: t('O funcionário recebe o mesmo valor todos os meses, não importa quantos serviços realizou. Pode ser combinado com comissões para compor o pagamento total.'),
    howItWorks: [
      t('Valor fixo definido por você (ex: R$2.000/mês)'),
      t('Se o mês tiver 20 ou 30 dias, o valor é proporcional'),
      t('Não depende da quantidade de serviços')
    ]
  },
  {
    id: 'commission_over_gross_revenue_executed',
    icon: 'pi pi-chart-bar',
    name: t('Comissão sobre Serviços Executados'),
    description: t('Percentual sobre os serviços que o funcionário realizou e entregou'),
    example: t('10% de R$45.000 em serviços = R$4.500'),
    detailedDescription: t('Operários ganhando comissão sobre o serviço que eles executaram. O funcionário recebe uma porcentagem sobre o valor dos serviços que ele mesmo executou.'),
    howItWorks: [
      t('Funcionário executa o serviço (ex: instalação, manutenção)'),
      t('Sistema calcula o valor total dos serviços executados por ele'),
      t('Ele recebe X% desse valor como comissão')
    ]
  },
  {
    id: 'commission_over_gross_revenue_sold_remotely',
    icon: 'pi pi-phone',
    name: t('Comissão sobre Vendas Remotas'),
    description: t('Percentual sobre vendas feitas remotamente (telefone, internet)'),
    example: t('12% de R$30.000 em vendas remotas = R$3.600'),
    detailedDescription: t('Atendentes ganhando comissão sobre compromissos agendados. Comissão sobre vendas realizadas remotamente, sem ir ao local do cliente.'),
    howItWorks: [
      t('Funcionário faz venda sem ir ao local (telefone/internet)'),
      t('Venda é contabilizada como "remota"'),
      t('Ele recebe X% do valor dessa venda')
    ]
  },
  {
    id: 'commission_over_gross_revenue_sold_in_loco',
    icon: 'pi pi-map-marker',
    name: t('Comissão sobre Vendas Presenciais'),
    description: t('Percentual sobre vendas feitas presencialmente no local do cliente'),
    example: t('15% de R$40.000 em vendas presenciais = R$6.000'),
    detailedDescription: t('Operadores ganhando comissão sobre serviços vendidos na casa do cliente. Comissão sobre vendas feitas pessoalmente no local do cliente.'),
    howItWorks: [
      t('Funcionário vai ao local do cliente'),
      t('Venda é contabilizada como "presencial" (in loco)'),
      t('Ele recebe X% do valor dessa venda')
    ]
  }
]);

const getSelectedRuleType = (): RuleTypeCard => {
  return ruleTypes.value.find(rt => rt.id === wizardData.value.rule_type) || ruleTypes.value[0];
};

const selectRuleType = (typeId: string): void => {
  wizardData.value.rule_type = typeId;
};
</script>

<style scoped>
.step-rule-type {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.step-description {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 1rem;
}

.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--purple-50);
  border-left: 4px solid var(--purple-500);
  border-radius: 6px;
}

.helper-banner i {
  color: var(--purple-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--purple-700);
}

.helper-content p {
  margin: 0 0 0.75rem 0;
  color: var(--purple-900);
  font-size: 0.9rem;
}

.rule-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.25rem;
}

.rule-type-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem 1rem;
  background: white;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.rule-type-card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.rule-type-card:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.rule-type-card.selected {
  border-color: var(--primary-color);
  background: var(--primary-50);
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  font-size: 1.5rem;
  color: var(--primary-color);
  background: var(--primary-50);
  border-radius: 8px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.rule-type-card.selected .card-icon {
  background: var(--primary-100);
  color: var(--primary-color);
}

.card-content {
  flex: 1;
  min-width: 0;
}

.card-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.card-indicator::before {
  content: '';
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.rule-type-card.selected .card-indicator::before {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.card-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color);
}

.card-description {
  margin: 0 0 0.75rem 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
}

.card-example {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: var(--surface-50);
  border-radius: 4px;
  font-size: 0.875rem;
}

.card-example strong {
  color: var(--text-color);
}

.card-example span {
  color: var(--text-color-secondary);
}

.selected-details {
  padding: 1.25rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
}

.details-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.details-header i {
  color: var(--blue-500);
  font-size: 1.25rem;
}

.details-header h3 {
  margin: 0;
  font-size: 1.125rem;
  color: var(--blue-700);
}

.details-content p {
  margin: 0 0 1rem 0;
  color: var(--blue-900);
  line-height: 1.6;
}

.details-example ul {
  margin: 0;
  padding-left: 1.25rem;
}

.details-example li {
  margin-bottom: 0.5rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .rule-types-grid {
    grid-template-columns: 1fr;
  }
}
</style>
